import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { Upload, Trash2, Image, Plus, Pencil, LogOut, Copy } from "lucide-react";
import { API, HTTP_UNAUTHORIZED, HTTP_FORBIDDEN, useAuth, ENTITY_SINGULAR, EntityForm } from "../shared";

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("events");
  const [data, setData] = useState({ events: [], ministries: [], media: [], cards: [], gallery: [], testimonies: [], subscribers: [] });
  const [dataLoading, setDataLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("general");
  const [galleryFilter, setGalleryFilter] = useState("all");
  const [dragActive, setDragActive] = useState(false);
  const [formState, setFormState] = useState({ open: false, entityType: null, initial: null });
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (location.state?.user) { fetchData(); return; }
    if (!loading && !user) { navigate('/'); }
    else if (user) { fetchData(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, navigate, location.state]);

  const fetchData = async () => {
    try {
      const [eventsRes, ministriesRes, mediaRes, cardsRes, galleryRes, testimoniesRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/admin/events`, { withCredentials: true }),
        axios.get(`${API}/admin/ministries`, { withCredentials: true }),
        axios.get(`${API}/admin/media`, { withCredentials: true }),
        axios.get(`${API}/admin/connect-cards`, { withCredentials: true }),
        axios.get(`${API}/admin/gallery`, { withCredentials: true }),
        axios.get(`${API}/admin/testimonies`, { withCredentials: true }),
        axios.get(`${API}/admin/live-alerts/subscribers`, { withCredentials: true }),
      ]);
      setData({
        events: eventsRes.data, ministries: ministriesRes.data, media: mediaRes.data,
        cards: cardsRes.data, gallery: galleryRes.data,
        testimonies: testimoniesRes.data, subscribers: subscribersRes.data,
      });
    } catch (error) {
      if (error.response?.status === HTTP_UNAUTHORIZED || error.response?.status === HTTP_FORBIDDEN) navigate('/');
    } finally { setDataLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    await uploadFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    let ok = 0; let fail = 0;
    for (const file of files) {
      if (!file.type.startsWith('image/')) { fail += 1; continue; }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', uploadCategory);
      try {
        await axios.post(`${API}/admin/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        ok += 1;
      } catch { fail += 1; }
    }
    if (ok) toast.success(`Uploaded ${ok} image${ok === 1 ? '' : 's'}${fail ? ` (${fail} failed)` : ''}`);
    if (!ok && fail) toast.error(`Upload failed for ${fail} file${fail === 1 ? '' : 's'}`);
    fetchData();
    setUploading(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) await uploadFiles(files);
  };

  const copyUrl = async (url) => {
    const full = `${window.location.origin}${url}`;
    try { await navigator.clipboard.writeText(full); toast.success('URL copied to clipboard'); }
    catch { toast.error('Copy failed'); }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try { await axios.delete(`${API}/admin/gallery/${imageId}`, { withCredentials: true }); toast.success('Image deleted'); fetchData(); }
    catch { toast.error('Failed to delete image'); }
  };

  const handleDeleteEntity = async (entityType, id) => {
    if (!window.confirm(`Delete this ${ENTITY_SINGULAR[entityType].toLowerCase()}? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/admin/${entityType}/${id}`, { withCredentials: true }); toast.success(`${ENTITY_SINGULAR[entityType]} deleted`); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const handleCardStatusChange = async (cardId, status) => {
    try { await axios.put(`${API}/admin/connect-cards/${cardId}/status`, { status }, { withCredentials: true }); toast.success('Status updated'); fetchData(); }
    catch { toast.error('Status update failed'); }
  };

  const handleTestimonyStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/testimonies/${id}/status`, { status }, { withCredentials: true });
      toast.success(status === 'approved' ? 'Testimony approved' : status === 'rejected' ? 'Testimony rejected' : 'Status updated');
      fetchData();
    } catch { toast.error('Status update failed'); }
  };

  const handleTestimonyDelete = async (id) => {
    if (!window.confirm('Delete this testimony? This cannot be undone.')) return;
    try { await axios.delete(`${API}/admin/testimonies/${id}`, { withCredentials: true }); toast.success('Testimony deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const openCreate = (entityType) => setFormState({ open: true, entityType, initial: null });
  const openEdit = (entityType, item) => setFormState({ open: true, entityType, initial: item });
  const closeForm = () => setFormState({ open: false, entityType: null, initial: null });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentUser = location.state?.user || user;
  const idKeyFor = (t) => t === "events" ? "event_id" : t === "ministries" ? "ministry_id" : "media_id";

  const renderEntityList = (entityType, renderMeta) => {
    const items = data[entityType] || [];
    const idKey = idKeyFor(entityType);
    return (
      <div data-testid={`admin-${entityType}-list`}>
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-black/40">{items.length} total</p>
          <button onClick={() => openCreate(entityType)} className="btn-dark flex items-center gap-2" data-testid={`admin-add-${entityType}`}>
            <Plus size={16} /> New {ENTITY_SINGULAR[entityType]}
          </button>
        </div>
        {items.length > 0 ? (
          <div className="space-y-0">
            {items.map((item, i) => (
              <div key={item[idKey]} className={`flex items-center justify-between py-6 ${i !== items.length - 1 ? 'border-b border-black/10' : ''}`}>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg truncate" style={{ fontFamily: 'Playfair Display, serif' }}>{item.title || item.name}</h3>
                  <p className="text-sm text-black/40 truncate">{renderMeta(item)}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => openEdit(entityType, item)} className="p-2 hover:bg-black/5" title="Edit" data-testid={`admin-edit-${entityType}-${item[idKey]}`}><Pencil size={16} /></button>
                  <button onClick={() => handleDeleteEntity(entityType, item[idKey])} className="p-2 hover:bg-red-50 text-red-600" title="Delete" data-testid={`admin-delete-${entityType}-${item[idKey]}`}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-center text-black/40 py-12">No {entityType}</p>}
      </div>
    );
  };

  return (
    <div className="pt-20 min-h-screen bg-neutral-50 fade-in">
      <div className="max-w-7xl mx-auto px-8 py-16">
        <div className="flex items-start justify-between mb-12">
          <div>
            <p className="heading-caps text-xs text-black/40 mb-2 tracking-[0.2em]">Welcome back</p>
            <h1 className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="admin-title">{currentUser?.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/whco-images.pdf" download="whco-site-images.pdf" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border border-black/20 hover:bg-black hover:text-white transition-colors"
              data-testid="admin-download-images-pdf"
            ><Image size={14} /> Image Inventory PDF</a>
            <button onClick={logout} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border border-black/20 hover:bg-black hover:text-white transition-colors" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-12 overflow-x-auto">
          {["events", "ministries", "media", "cards", "testimonies", "subscribers", "gallery"].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-xs uppercase tracking-widest whitespace-nowrap transition-all ${activeTab === tab ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"}`}
              data-testid={`admin-tab-${tab}`}
            >{tab} ({data[tab]?.length || 0})</button>
          ))}
        </div>

        {dataLoading ? (
          <div className="text-center py-12"><div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto"></div></div>
        ) : (
          <div className="bg-white p-8">
            {activeTab === "events" && renderEntityList("events", (e) => `${new Date(e.date).toLocaleString()} • ${e.event_type}${e.is_recurring ? ' • recurring' : ''}`)}
            {activeTab === "ministries" && renderEntityList("ministries", (m) => `${m.category}${m.is_active ? ' • active' : ' • inactive'}`)}
            {activeTab === "media" && renderEntityList("media", (m) => `${new Date(m.date).toLocaleDateString()} • ${m.media_type}${m.is_featured ? ' • featured' : ''}`)}
            {activeTab === "cards" && (
              <div className="space-y-0" data-testid="admin-cards-list">
                {data.cards.length > 0 ? data.cards.map((card, i) => (
                  <div key={card.card_id} className={`flex items-center justify-between py-6 ${i !== data.cards.length - 1 ? 'border-b border-black/10' : ''}`}>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>{card.name}</h3>
                      <p className="text-sm text-black/40 truncate">{card.email} • {card.card_type}{card.message ? ` — ${card.message}` : ''}</p>
                    </div>
                    <select value={card.status} onChange={(e) => handleCardStatusChange(card.card_id, e.target.value)}
                      className="ml-4 px-3 py-2 border border-black/20 bg-white text-xs uppercase tracking-widest" data-testid={`card-status-${card.card_id}`}
                    >
                      <option value="new">new</option>
                      <option value="contacted">contacted</option>
                      <option value="completed">completed</option>
                    </select>
                  </div>
                )) : <p className="text-center text-black/40 py-12">No connect cards</p>}
              </div>
            )}
            {activeTab === "testimonies" && (
              <div className="space-y-0" data-testid="admin-testimonies-list">
                {data.testimonies.length > 0 ? data.testimonies.map((t, i) => (
                  <div key={t.testimony_id} className={`py-6 ${i !== data.testimonies.length - 1 ? 'border-b border-black/10' : ''}`} data-testid={`admin-testimony-${t.testimony_id}`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>{t.name}</h3>
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${
                            t.status === 'approved' ? 'bg-green-100 text-green-800' :
                            t.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>{t.status}</span>
                        </div>
                        <p className="text-xs text-black/40">
                          {t.email || '—'}{t.location ? ` • ${t.location}` : ''} • {new Date(t.created_at).toLocaleDateString()}
                        </p>
                        {t.title && <p className="text-sm mt-2 text-black/70 italic">{t.title}</p>}
                        <p className="text-sm mt-2 text-black/60 leading-relaxed whitespace-pre-wrap">{t.content}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {t.status !== 'approved' && (
                        <button onClick={() => handleTestimonyStatus(t.testimony_id, 'approved')}
                          className="px-4 py-2 bg-black text-white text-xs uppercase tracking-widest hover:bg-neutral-800"
                          data-testid={`approve-testimony-${t.testimony_id}`}
                        >Approve</button>
                      )}
                      {t.status !== 'rejected' && (
                        <button onClick={() => handleTestimonyStatus(t.testimony_id, 'rejected')}
                          className="px-4 py-2 bg-white border border-black/20 text-black text-xs uppercase tracking-widest hover:bg-neutral-100"
                          data-testid={`reject-testimony-${t.testimony_id}`}
                        >Reject</button>
                      )}
                      {t.status !== 'pending' && (
                        <button onClick={() => handleTestimonyStatus(t.testimony_id, 'pending')}
                          className="px-4 py-2 bg-white border border-black/20 text-black/70 text-xs uppercase tracking-widest hover:bg-neutral-100"
                          data-testid={`repending-testimony-${t.testimony_id}`}
                        >Mark Pending</button>
                      )}
                      <button onClick={() => handleTestimonyDelete(t.testimony_id)}
                        className="px-4 py-2 text-red-700 text-xs uppercase tracking-widest hover:bg-red-50 inline-flex items-center gap-2"
                        data-testid={`delete-testimony-${t.testimony_id}`}
                      ><Trash2 size={14} /> Delete</button>
                    </div>
                  </div>
                )) : <p className="text-center text-black/40 py-12">No testimonies yet</p>}
              </div>
            )}
            {activeTab === "subscribers" && (
              <div className="space-y-0" data-testid="admin-subscribers-list">
                <div className="mb-6 text-xs uppercase tracking-widest text-black/40">
                  Live-stream alert list — email blast sent when YouTube goes live.
                </div>
                {data.subscribers.length > 0 ? data.subscribers.map((s, i) => (
                  <div key={s.subscriber_id || s.email} className={`flex items-center justify-between py-4 ${i !== data.subscribers.length - 1 ? 'border-b border-black/10' : ''}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{s.name || '—'} <span className="text-black/40">• {s.email}</span></p>
                      <p className="text-xs text-black/40">
                        {s.source || 'web'} • joined {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                      </p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${s.is_active === false ? 'bg-neutral-100 text-black/40' : 'bg-green-100 text-green-800'}`}>
                      {s.is_active === false ? 'unsubscribed' : 'active'}
                    </span>
                  </div>
                )) : <p className="text-center text-black/40 py-12">No subscribers yet</p>}
              </div>
            )}
            {activeTab === "gallery" && (
              <div data-testid="admin-gallery">
                <div onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  className={`mb-6 p-8 border-2 border-dashed rounded-lg transition-all ${dragActive ? "border-black bg-neutral-100" : "border-black/20 bg-neutral-50 hover:border-black/40"}`}
                  data-testid="upload-dropzone"
                >
                  <div className="text-center">
                    <Upload size={32} className={`mx-auto mb-3 ${dragActive ? "text-black" : "text-black/40"}`} />
                    <p className="text-base mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{dragActive ? "Drop images to upload" : "Drag & drop images here"}</p>
                    <p className="text-xs text-black/40 mb-4">or click to choose files (JPG, PNG, GIF, WebP — multi-select supported)</p>
                    <div className="flex flex-wrap gap-3 items-center justify-center">
                      <select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}
                        className="px-4 py-2 border border-black/20 bg-white text-sm" data-testid="upload-category"
                      >
                        <option value="general">General</option>
                        <option value="worship">Worship</option>
                        <option value="events">Events</option>
                        <option value="ministries">Ministries</option>
                        <option value="pastors">Pastors</option>
                      </select>
                      <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" multiple className="hidden" data-testid="upload-input" />
                      <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                        className="btn-dark flex items-center gap-2 disabled:opacity-50" data-testid="upload-button"
                      ><Upload size={16} />{uploading ? 'Uploading…' : 'Choose Images'}</button>
                    </div>
                  </div>
                </div>

                {data.gallery.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6" data-testid="gallery-filter-row">
                    {["all", "general", "worship", "events", "ministries", "pastors"].map((cat) => {
                      const count = cat === "all" ? data.gallery.length : data.gallery.filter((g) => g.category === cat).length;
                      const active = galleryFilter === cat;
                      return (
                        <button key={cat} onClick={() => setGalleryFilter(cat)}
                          className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-all ${active ? "bg-black text-white" : "bg-neutral-100 text-black/60 hover:bg-neutral-200"}`}
                          data-testid={`gallery-filter-${cat}`}
                        >{cat} <span className="opacity-50 ml-1">{count}</span></button>
                      );
                    })}
                  </div>
                )}

                {(() => {
                  const filtered = galleryFilter === "all" ? data.gallery : data.gallery.filter((g) => g.category === galleryFilter);
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-20 bg-neutral-100" data-testid="gallery-empty">
                        <Image size={48} className="mx-auto text-black/20 mb-4" />
                        <p className="text-black/40">{data.gallery.length === 0 ? "No images uploaded yet" : `No images in "${galleryFilter}"`}</p>
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
                      {filtered.map((image) => (
                        <div key={image.image_id} className="relative group bg-neutral-100 overflow-hidden" data-testid={`gallery-item-${image.image_id}`}>
                          <div className="aspect-square overflow-hidden">
                            <img src={image.url} alt={image.caption || image.filename}
                              className="w-full h-full object-cover transition-transform group-hover:scale-105" loading="lazy"
                            />
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                            <div className="flex justify-end gap-2">
                              <button onClick={() => copyUrl(image.url)} className="p-2 bg-white text-black rounded-full hover:bg-neutral-200" title="Copy URL" data-testid={`copy-url-${image.image_id}`}><Copy size={14} /></button>
                              <button onClick={() => handleDeleteImage(image.image_id)} className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700" title="Delete" data-testid={`delete-image-${image.image_id}`}><Trash2 size={14} /></button>
                            </div>
                            <div className="text-white text-[10px]">
                              <p className="uppercase tracking-wider opacity-70">{image.category}</p>
                              <p className="truncate font-medium">{image.filename}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>
      {formState.open && (
        <EntityForm entityType={formState.entityType} initial={formState.initial} onClose={closeForm} onSaved={fetchData} />
      )}
    </div>
  );
};

export default AdminDashboard;
